import { apiGet, apiPost, apiPut, apiDelete, unwrap, getTenantId, setTenantId } from './apiClient';
import { getCurrentUserEmail } from '@/lib/auth';
import {
  ResponseDTO, Tag, TagResourcesCountDTO, DeviceSummaryDTO,
  AppInfoDTO, PolicyInfoDTO, DynamicTagParamDTO,
} from '@/types/api';

// --- CRUD ---

/**
 * The backend tag schema is snake_case and the working production payload is:
 *   { tag_id: "", tag_name, tag_description, tag_type: "STATIC"|"DYNAMIC",
 *     tag_criteria, tag_color, tag_icon, created_by }
 * Tenant scoping is implicit via the JWT — do NOT send `customers` in the body
 * and do NOT send an `X-Tenant-Id` header (the working request omits it).
 */
export interface CreateTagInput {
  name: string;
  description?: string;
  type: "static" | "dynamic";
  color: string;
  icon?: string | null;
  criteria?: unknown;
}

function serializeCriteria(criteria: unknown): string | null {
  if (criteria == null) return null;
  if (typeof criteria === "string") return criteria;
  try {
    return JSON.stringify(criteria);
  } catch {
    return null;
  }
}

function toBackendTagBody(input: CreateTagInput, id?: string): Record<string, unknown> {
  const body: Record<string, unknown> = {
    tag_name: input.name,
    tag_description: input.description ?? "",
    tag_type: input.type.toUpperCase(),
    tag_criteria: serializeCriteria(input.criteria),
    tag_color: input.color,
    tag_icon: input.icon ?? null,
    created_by: getCurrentUserEmail() ?? "",
  };
  if (id) body.tag_id = id;
  return body;
}

export async function createTag(tag: Tag): Promise<string> {
  const res = await apiPost<ResponseDTO<null>>('/api/v1/tag', tag);
  return res.message || 'Tag created';
}

/**
 * Update a tag using the same snake_case backend schema as create.
 * The id is sent in `tag_id` (the working portal uses PUT /api/v1/tag).
 */
export async function updateTagWithInput(
  tagId: string,
  input: CreateTagInput
): Promise<string> {
  const body = toBackendTagBody(input, tagId);
  const res = await apiPut<ResponseDTO<null>>('/api/v1/tag', body);
  return res.message || 'Tag updated';
}

/**
 * Create the same tag for one or more tenants. The backend uses the JWT for
 * tenant scoping, so to target a different tenant we temporarily swap the
 * stored tenant id (which is also embedded in the partner JWT context).
 * For the primary single-tenant case this is a single POST.
 */
export async function createTagForTenants(
  tenantIds: string[],
  input: CreateTagInput
): Promise<Array<{ tenantId: string; ok: boolean; error?: string }>> {
  const body = toBackendTagBody(input);
  const results: Array<{ tenantId: string; ok: boolean; error?: string }> = [];
  // Single-tenant or no explicit tenant: rely on JWT scoping (no header).
  if (tenantIds.length === 0) {
    try {
      await apiPost<ResponseDTO<null>>('/api/v1/tag', body);
      return [{ tenantId: '', ok: true }];
    } catch (err: any) {
      return [{ tenantId: '', ok: false, error: err?.message || String(err) }];
    }
  }
  for (const tenantId of tenantIds) {
    try {
      await apiPost<ResponseDTO<null>>('/api/v1/tag', body, {
        headers: { 'X-Tenant-Id': tenantId },
      });
      results.push({ tenantId, ok: true });
    } catch (err: any) {
      results.push({ tenantId, ok: false, error: err?.message || String(err) });
    }
  }
  return results;
}

export async function updateTag(tag: Tag): Promise<string> {
  const res = await apiPut<ResponseDTO<null>>('/api/v1/tag', tag);
  return res.message || 'Tag updated';
}

function flattenTagsResponse(data: unknown): Tag[] {
  if (!data || !Array.isArray(data)) return [];
  // Grouped shape: [{ tenant_id, tags: [...] }, ...]
  if (data.length > 0 && data[0] && typeof data[0] === 'object' && Array.isArray((data[0] as any).tags)) {
    return (data as Array<{ tenant_id?: string; tags?: Tag[] }>).flatMap((g) =>
      (g.tags ?? []).map((t) => ({ tenant_id: g.tenant_id, ...t } as Tag))
    );
  }
  return data as Tag[];
}

export async function listTags(): Promise<Tag[]> {
  const res = await apiGet<ResponseDTO<unknown>>('/api/v1/tags');
  return flattenTagsResponse(unwrap(res));
}

/** Fetch tags scoped to a specific tenant by temporarily setting the global tenant ID */
export async function listTagsByTenant(tenantId: string): Promise<Tag[]> {
  const previousTenantId = getTenantId();
  try {
    setTenantId(tenantId);
    const res = await apiGet<ResponseDTO<unknown>>('/api/v1/tags', {
      includeTenantHeader: true,
    });
    const flat = flattenTagsResponse(unwrap(res));
    return flat.filter((t) => !t.tenant_id || t.tenant_id === tenantId);
  } finally {
    if (previousTenantId) {
      setTenantId(previousTenantId);
    } else {
      localStorage.removeItem('smplify_tenant_id');
    }
  }
}

export async function getTagByName(name: string): Promise<Tag> {
  const res = await apiGet<ResponseDTO<Tag>>(`/api/v1/tag/name/${encodeURIComponent(name)}`);
  return unwrap(res);
}

export async function getTagById(id: string): Promise<Tag> {
  const res = await apiGet<ResponseDTO<Tag>>(`/api/v1/tag/Id/${id}`);
  return unwrap(res);
}

export async function deleteTagByName(name: string): Promise<void> {
  await apiDelete<void>(`/api/v1/tag/name/${encodeURIComponent(name)}`);
}

export async function deleteTagById(id: string): Promise<void> {
  await apiDelete<void>(`/api/v1/tag/Id/${id}`);
}

// --- Tag queries ---

export async function getTagsByDevice(deviceId: string): Promise<Tag[]> {
  const res = await apiGet<ResponseDTO<Tag[]>>(`/api/v1/tags/device/${deviceId}`);
  return unwrap(res);
}

export async function getTagsByApp(appId: string): Promise<Tag[]> {
  const res = await apiGet<ResponseDTO<Tag[]>>(`/api/v1/tags/app/${appId}`);
  return unwrap(res);
}

export async function getTagsByPolicy(policyId: string): Promise<Tag[]> {
  const res = await apiGet<ResponseDTO<Tag[]>>(`/api/v1/tags/policy/${policyId}`);
  return unwrap(res);
}

export async function getTagResourceCounts(tagId: string): Promise<TagResourcesCountDTO> {
  const res = await apiGet<ResponseDTO<TagResourcesCountDTO>>(`/api/v1/tags/${tagId}/resources/count`);
  return unwrap(res);
}

export async function getDevicesByTag(tagId: string): Promise<DeviceSummaryDTO[]> {
  const res = await apiGet<ResponseDTO<DeviceSummaryDTO[]>>(`/api/v1/tags/${tagId}/devices`);
  return unwrap(res);
}

export async function getAppsByTag(tagId: string): Promise<AppInfoDTO[]> {
  const res = await apiGet<ResponseDTO<AppInfoDTO[]>>(`/api/v1/tags/${tagId}/apps`);
  return unwrap(res);
}

export async function getPoliciesByTag(tagId: string): Promise<PolicyInfoDTO[]> {
  const res = await apiGet<ResponseDTO<PolicyInfoDTO[]>>(`/api/v1/tags/${tagId}/policies`);
  return unwrap(res);
}

export async function getDynamicTagParams(targetType: string): Promise<DynamicTagParamDTO[]> {
  const res = await apiGet<ResponseDTO<DynamicTagParamDTO[]>>(`/api/v1/tag/target-type/${targetType}`);
  return unwrap(res);
}
